/**
 * Geo Tile Map
 *
 * Shared Web Mercator tile helpers for visual artifact map viewers.
 *
 * Primary responsibilities:
 * - Project coordinates into SVG viewport pixels
 * - Render OpenStreetMap raster tiles behind SVG overlays
 *
 * Dependencies:
 * - OpenStreetMap tile service
 */

export type GeoCoordinate = {
  latitude: number
  longitude: number
}

export type GeoBounds = {
  minLatitude: number
  maxLatitude: number
  minLongitude: number
  maxLongitude: number
}

export type GeoViewport = {
  width: number
  height: number
}

export type GeoPoint = {
  x: number
  y: number
}

export type GeoTile = {
  id: string
  href: string
  x: number
  y: number
}

export type GeoTileMap = {
  bounds: GeoBounds
  center: GeoCoordinate
  zoom: number
  topLeftWorld: GeoPoint
  viewport: GeoViewport
  tiles: GeoTile[]
}

const tileSize = 256
const minZoom = 2
const maxZoom = 18
const minimumSpan = 0.002
const tileHost = 'https://tile.openstreetmap.org'

// --------------

export function getGeoBounds(
  coordinates: GeoCoordinate[],
  fallbackCenter?: GeoCoordinate,
  paddingRatio = 0.18
): GeoBounds {
  const allCoordinates = coordinates.length > 0
    ? coordinates
    : fallbackCenter
      ? [fallbackCenter]
      : [{ latitude: 0, longitude: 0 }]
  const latitudeValues = allCoordinates.map((coordinate) => coordinate.latitude)
  const longitudeValues = allCoordinates.map((coordinate) => coordinate.longitude)
  const minLatitude = Math.min(...latitudeValues)
  const maxLatitude = Math.max(...latitudeValues)
  const minLongitude = Math.min(...longitudeValues)
  const maxLongitude = Math.max(...longitudeValues)
  const latitudeCenter = (minLatitude + maxLatitude) / 2
  const longitudeCenter = (minLongitude + maxLongitude) / 2
  const latitudeSpan = Math.max(maxLatitude - minLatitude, minimumSpan)
  const longitudeSpan = Math.max(maxLongitude - minLongitude, minimumSpan)
  const paddedLatitudeSpan = latitudeSpan * (1 + paddingRatio * 2)
  const paddedLongitudeSpan = longitudeSpan * (1 + paddingRatio * 2)

  return {
    minLatitude: clampLatitude(latitudeCenter - paddedLatitudeSpan / 2),
    maxLatitude: clampLatitude(latitudeCenter + paddedLatitudeSpan / 2),
    minLongitude: clampLongitude(longitudeCenter - paddedLongitudeSpan / 2),
    maxLongitude: clampLongitude(longitudeCenter + paddedLongitudeSpan / 2)
  }
}

export function createGeoTileMap(bounds: GeoBounds, viewport: GeoViewport): GeoTileMap {
  const safeViewport = {
    width: Math.max(viewport.width, 1),
    height: Math.max(viewport.height, 1)
  }
  const center = getBoundsCenter(bounds)
  const zoom = getFittingZoom(bounds, safeViewport)
  const centerWorld = lngLatToWorld(center, zoom)
  const topLeftWorld = {
    x: centerWorld.x - safeViewport.width / 2,
    y: centerWorld.y - safeViewport.height / 2
  }

  return {
    bounds,
    center,
    zoom,
    topLeftWorld,
    viewport: safeViewport,
    tiles: getVisibleTiles(zoom, topLeftWorld, safeViewport)
  }
}

export function projectGeoCoordinate(coordinate: GeoCoordinate, tileMap: GeoTileMap): GeoPoint {
  const world = lngLatToWorld(coordinate, tileMap.zoom)

  return {
    x: world.x - tileMap.topLeftWorld.x,
    y: world.y - tileMap.topLeftWorld.y
  }
}

export function GeoTileLayer({ tileMap }: { tileMap: GeoTileMap }) {
  return (
    <g aria-hidden="true">
      <rect width={tileMap.viewport.width} height={tileMap.viewport.height} fill="#e5edf2" />
      {tileMap.tiles.map((tile) => (
        <image
          key={tile.id}
          href={tile.href}
          x={tile.x}
          y={tile.y}
          width={tileSize}
          height={tileSize}
          preserveAspectRatio="none"
        />
      ))}
      <rect
        width={tileMap.viewport.width}
        height={tileMap.viewport.height}
        fill="rgba(255,255,255,0.16)"
      />
      <text
        x={Math.max(tileMap.viewport.width - 8, 0)}
        y={Math.max(tileMap.viewport.height - 8, 12)}
        fill="#475569"
        fontSize="10"
        textAnchor="end"
      >
        (C) OpenStreetMap contributors
      </text>
    </g>
  )
}

// --------------

function getFittingZoom(bounds: GeoBounds, viewport: GeoViewport) {
  const northWest = lngLatToWorld(
    {
      latitude: bounds.maxLatitude,
      longitude: bounds.minLongitude
    },
    0
  )
  const southEast = lngLatToWorld(
    {
      latitude: bounds.minLatitude,
      longitude: bounds.maxLongitude
    },
    0
  )
  const spanX = Math.max(Math.abs(southEast.x - northWest.x), 0.000001)
  const spanY = Math.max(Math.abs(southEast.y - northWest.y), 0.000001)
  const scale = Math.min(viewport.width / spanX, viewport.height / spanY)

  return clamp(Math.floor(Math.log2(scale)), minZoom, maxZoom)
}

function getVisibleTiles(zoom: number, topLeftWorld: GeoPoint, viewport: GeoViewport): GeoTile[] {
  const tileCount = 2 ** zoom
  const startX = Math.floor(topLeftWorld.x / tileSize)
  const endX = Math.floor((topLeftWorld.x + viewport.width) / tileSize)
  const startY = Math.max(Math.floor(topLeftWorld.y / tileSize), 0)
  const endY = Math.min(Math.floor((topLeftWorld.y + viewport.height) / tileSize), tileCount - 1)
  const tiles: GeoTile[] = []

  for (let tileX = startX; tileX <= endX; tileX += 1) {
    for (let tileY = startY; tileY <= endY; tileY += 1) {
      const wrappedTileX = modulo(tileX, tileCount)

      tiles.push({
        id: `${zoom}:${wrappedTileX}:${tileY}`,
        href: `${tileHost}/${zoom}/${wrappedTileX}/${tileY}.png`,
        x: tileX * tileSize - topLeftWorld.x,
        y: tileY * tileSize - topLeftWorld.y
      })
    }
  }

  return tiles
}

function getBoundsCenter(bounds: GeoBounds): GeoCoordinate {
  return {
    latitude: (bounds.minLatitude + bounds.maxLatitude) / 2,
    longitude: (bounds.minLongitude + bounds.maxLongitude) / 2
  }
}

function lngLatToWorld(coordinate: GeoCoordinate, zoom: number): GeoPoint {
  const scale = tileSize * 2 ** zoom
  const latitude = clampLatitude(coordinate.latitude)
  const longitude = clampLongitude(coordinate.longitude)
  const sinLatitude = Math.sin((latitude * Math.PI) / 180)

  return {
    x: ((longitude + 180) / 360) * scale,
    y:
      (0.5 -
        Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)) *
      scale
  }
}

function clampLatitude(value: number) {
  return clamp(value, -85.05112878, 85.05112878)
}

function clampLongitude(value: number) {
  return clamp(value, -180, 180)
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function modulo(value: number, divisor: number) {
  return ((value % divisor) + divisor) % divisor
}
